import * as Sentry from '@sentry/react';
import { config } from './environment';

export const initSentry = () => {
  if (!config.enableErrorTracking || !config.sentryDsn) {
    console.log('Sentry error tracking is disabled');
    return;
  }

  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.env,
    // A2 (R18): the release is the build's commit (from vite define), so errors split by build.
    release: __APP_VERSION__.commit,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring
    tracesSampleRate: config.env === 'production' ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Error filtering
    beforeSend(event, hint) {
      // Filter out errors from browser extensions
      if (event.exception) {
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message);
          if (message.includes('chrome-extension://') || message.includes('moz-extension://')) {
            return null;
          }
        }
      }
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network request failed',
    ],
  });
};

// Helper to capture errors manually
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (!config.enableErrorTracking) return;
  
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to capture messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  if (!config.enableErrorTracking) return;
  
  Sentry.captureMessage(message, level);
};

// Helper to set user context
export const setUser = (user: { id: string; email?: string; username?: string }) => {
  if (!config.enableErrorTracking) return;
  
  Sentry.setUser(user);
};

// Helper to add breadcrumb
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  if (!config.enableErrorTracking) return;
  
  Sentry.addBreadcrumb(breadcrumb);
};
